import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AxiosError, isAxiosError } from 'axios';
import { authApiClient } from '../../../api/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { EMAIL_SETTINGS_QUERY_KEY, formatProviderTestError } from '../constants/emailSettings';

export const fetchEmailSettings = async () => {
  const { data } = await authApiClient.get('/organization/settings/email-settings');
  return data;
};

export const updateEmailSettings = async (payload) => {
  const { data } = await authApiClient.patch('/organization/settings/email-settings', payload);
  return data;
};

export const testEmailSettings = async (payload) => {
  const { data } = await authApiClient.post('/organization/settings/email-settings/test', payload);
  return data;
};

export const useGetEmailSettingsHandler = ({ enabled = true } = {}) => {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: EMAIL_SETTINGS_QUERY_KEY,
    queryFn: fetchEmailSettings,
    enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (!query.isError) return;

    if (isAxiosError(query.error)) {
      dispatch(
        showSnackbar({
          message: query.error.response?.data?.errors?.message || query.error.message,
          severity: 'error',
        }),
      );
      return;
    }

    dispatch(
      showSnackbar({
        message: 'Failed to fetch email settings',
        severity: 'error',
      }),
    );
  }, [dispatch, query.error, query.isError]);

  return {
    emailSettings: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export const useUpdateEmailSettingsHandler = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: updateEmailSettings,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(EMAIL_SETTINGS_QUERY_KEY, data);
      dispatch(
        showSnackbar({
          message: 'Email settings updated successfully',
          severity: 'success',
        }),
      );
      onSuccess && onSuccess(data, variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.errors?.message || error.message,
            severity: 'error',
          }),
        );
        return;
      }

      dispatch(
        showSnackbar({
          message: 'Failed to update email settings',
          severity: 'error',
        }),
      );
    },
  });

  return {
    onSubmit: (payload) => mutation.mutate(payload),
    isPending: mutation.isPending,
  };
};

export const useTestEmailSettingsHandler = ({ onSuccess, onError } = {}) => {
  const mutation = useMutation({
    mutationFn: testEmailSettings,
    onSuccess: (data) => {
      onSuccess && onSuccess(data);
    },
    onError: (error) => {
      onError && onError(formatProviderTestError(error));
    },
  });

  return {
    onTest: (payload) => mutation.mutate(payload),
    isPending: mutation.isPending,
  };
};
